/**
 * Performance monitoring utilities using User Timing API
 * Measures real-world performance during key user experiences
 */

export class PerformanceMonitor {
  private static instance: PerformanceMonitor;

  private constructor() {
    this.setupPerformanceObserver();
  }

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  /**
   * Mark the start of a performance measurement
   */
  mark(name: string): void {
    try {
      performance.mark(name);
    } catch (error) {
      console.warn(`Failed to mark performance: ${name}`, error);
    }
  }

  /**
   * Measure the duration between two marks or from a mark to now
   */
  measure(name: string, startMark: string, endMark?: string): void {
    try {
      if (endMark) {
        performance.measure(name, startMark, endMark);
      } else {
        performance.measure(name, startMark);
      }
      
      // Get the measurement
      const measures = performance.getEntriesByName(name, 'measure');
      if (measures.length > 0) {
        const measure = measures[measures.length - 1];
        console.log(`⏱️ ${name}: ${measure.duration.toFixed(2)}ms`);
      }
    } catch (error) {
      console.warn(`Failed to measure performance: ${name}`, error);
    }
  }

  /**
   * Setup Performance Observer to automatically log measurements
   */
  private setupPerformanceObserver(): void {
    if (typeof PerformanceObserver === 'undefined') {
      console.warn('PerformanceObserver not supported');
      return;
    }

    try {
      // Observe user timing measurements
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'measure') {
            // Send to analytics or monitoring service
            this.reportMetric({
              name: entry.name,
              duration: entry.duration,
              startTime: entry.startTime,
            });
          }
        }
      });

      observer.observe({ entryTypes: ['measure', 'navigation', 'resource'] });
    } catch (error) {
      console.warn('Failed to setup PerformanceObserver', error);
    }
  }

  /**
   * Report metrics to analytics (can be extended to send to monitoring services)
   */
  private reportMetric(metric: { name: string; duration: number; startTime: number }): void {
    // Log to console in development
    if (import.meta.env.DEV) {
      console.log('📊 Performance Metric:', metric);
    }

    // Send to Google Analytics or other monitoring services
    if (typeof window !== 'undefined' && 'gtag' in window) {
      (window as any).gtag('event', 'timing_complete', {
        name: metric.name,
        value: Math.round(metric.duration),
        event_category: 'Performance',
      });
    }
  }

  /**
   * Track a user interaction
   */
  trackInteraction(name: string, callback: () => void | Promise<void>): void {
    const startMark = `${name}-start`;
    const endMark = `${name}-end`;

    this.mark(startMark);
    
    const result = callback();
    
    if (result instanceof Promise) {
      result.finally(() => {
        this.mark(endMark);
        this.measure(name, startMark, endMark);
      });
    } else {
      this.mark(endMark);
      this.measure(name, startMark, endMark);
    }
  }

  /**
   * Get Core Web Vitals and other performance metrics
   */
  getCoreWebVitals(): void {
    if (typeof window === 'undefined') return;

    // LCP - Largest Contentful Paint
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      console.log('🎯 LCP:', lastEntry.startTime.toFixed(2), 'ms');
      this.reportMetric({
        name: 'LCP',
        duration: lastEntry.startTime,
        startTime: 0,
      });
    }).observe({ type: 'largest-contentful-paint', buffered: true });

    // FID - First Input Delay
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: any) => {
        console.log('👆 FID:', entry.processingStart - entry.startTime, 'ms');
        this.reportMetric({
          name: 'FID',
          duration: entry.processingStart - entry.startTime,
          startTime: entry.startTime,
        });
      });
    }).observe({ type: 'first-input', buffered: true });

    // CLS - Cumulative Layout Shift
    let clsValue = 0;
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as any[]) {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      }
      console.log('📐 CLS:', clsValue.toFixed(3));
      this.reportMetric({
        name: 'CLS',
        duration: clsValue,
        startTime: 0,
      });
    }).observe({ type: 'layout-shift', buffered: true });
  }

  /**
   * Clear all performance marks and measures
   */
  clear(): void {
    performance.clearMarks();
    performance.clearMeasures();
  }
}

// Export singleton instance
export const performanceMonitor = PerformanceMonitor.getInstance();

// Export helper functions
export const markPerformance = (name: string) => performanceMonitor.mark(name);
export const measurePerformance = (name: string, startMark: string, endMark?: string) => 
  performanceMonitor.measure(name, startMark, endMark);
export const trackInteraction = (name: string, callback: () => void | Promise<void>) => 
  performanceMonitor.trackInteraction(name, callback);
